
import { Receipt, AlertTriangle, CreditCard } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useParentInvoices } from '@/hooks/use-invoices';
import { Link } from 'react-router-dom';

import { formatShortDate as formatDate, formatCurrency } from '@/lib/format';

export function OutstandingInvoicesBanner() {
  const { data, isLoading } = useParentInvoices();

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="pb-3">
          <Skeleton className="h-6 w-32" />
        </CardHeader>
        <CardContent>
          <Skeleton className="h-16 w-full" />
        </CardContent>
      </Card>
    );
  }

  const outstandingInvoices = data?.invoices.filter(
    inv => inv.status === 'sent' || inv.status === 'overdue'
  ) ?? [];

  if (outstandingInvoices.length === 0) {
    return null; // Don't show if no outstanding invoices
  }

  const hasOverdue = outstandingInvoices.some(inv => inv.status === 'overdue');

  return (
    <Card className={cn(
      'border-l-4',
      hasOverdue
        ? 'border-l-red-500 bg-red-50 dark:bg-red-950/30'
        : 'border-l-amber-500 bg-amber-50 dark:bg-amber-950/30'
    )}>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-lg">
          {hasOverdue ? (
            <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400" />
          ) : (
            <Receipt className="w-5 h-5 text-amber-600 dark:text-amber-400" />
          )}
          {hasOverdue ? 'Payment Overdue' : 'Outstanding Invoices'}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {outstandingInvoices.slice(0, 3).map((invoice) => {
          const isOverdue = invoice.status === 'overdue' || new Date(invoice.dueDate) < new Date();

          return (
            <div
              key={invoice.id}
              className="flex items-center justify-between p-3 bg-white dark:bg-white/5 rounded-lg"
            >
              <div>
                <p className="font-medium text-foreground">
                  {invoice.description || 'Invoice'}
                </p>
                <p className={cn(
                  'text-sm',
                  isOverdue ? 'text-red-600 dark:text-red-400' : 'text-muted-foreground'
                )}>
                  Due: {formatDate(invoice.dueDate)}
                  {isOverdue && ' (Overdue)'}
                </p>
              </div>
              <div className="text-right">
                <p className="font-bold text-foreground">
                  {formatCurrency(invoice.amountCents, invoice.currency)}
                </p>
              </div>
            </div>
          );
        })}

        <div className="flex items-center justify-between pt-2 border-t border-border">
          <div>
            <p className="text-sm text-muted-foreground">Total Outstanding</p>
            <p className="text-xl font-bold text-foreground">
              {formatCurrency(data?.totalOutstanding ?? 0)}
            </p>
          </div>
          <Link to="/parent/invoices">
            <Button className={cn(
              hasOverdue ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'
            )}>
              <CreditCard className="w-4 h-4 mr-2" />
              View & Pay
            </Button>
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}
